import React, { useEffect, useState } from "react";
import { History, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ago = (iso) => {
  const yrs = (Date.now() - new Date(iso).getTime()) / (365.25 * 86400e3);
  if (yrs < 1 / 12) return "this month";
  if (yrs < 1) return `${Math.round(yrs * 12)}mo ago`;
  return `${yrs.toFixed(1)}y ago`;
};

/** Timeline of every logged 12V/16V replacement — the owner's provable
 *  service history (Tesla exposes none via API). Read-only on the public
 *  passport; editable (delete mistakes) inside the app. */
export const LvHistoryLog = ({ vin, refreshKey = 0, canEdit = false, onChanged, framed = false, liIonLabel = "Lithium-ion" }) => {
  const [entries, setEntries] = useState([]);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!vin) return;
    fetch(`${API}/api/lv/history/${encodeURIComponent(vin)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setEntries(d?.entries || []))
      .catch(() => {});
  }, [vin, refreshKey]);

  const remove = async (replaced_at) => {
    if (deleting) return;
    setDeleting(replaced_at);
    try {
      const r = await fetch(
        `${API}/api/lv/history/${encodeURIComponent(vin)}?replaced_at=${encodeURIComponent(replaced_at)}`,
        { method: "DELETE" },
      );
      if (r.ok) {
        const body = await r.json();
        setEntries((prev) => prev.filter((e) => e.replaced_at !== replaced_at));
        onChanged?.(body.current);
      }
    } catch (e) {} finally {
      setDeleting(null);
    }
  };

  if (!vin || entries.length === 0) return null;

  return (
    <div
      className={framed ? "rounded-xl border p-4 mb-4" : "mt-3"}
      style={framed ? { borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" } : undefined}
      data-testid="lv-history-log"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <History className="w-3 h-3 text-zinc-500" strokeWidth={2.4} />
        <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500">
          Replacement history
        </span>
        <span className="font-mono text-[9px] text-zinc-600">· {entries.length}</span>
      </div>
      <div className="space-y-0">
        {entries.map((e, i) => (
          <div key={e.replaced_at} className="flex items-start gap-2.5" data-testid={`lv-history-entry-${i}`}>
            <div className="flex flex-col items-center self-stretch shrink-0 pt-1">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ background: i === 0 ? "#39FF14" : "rgba(255,255,255,0.25)", boxShadow: i === 0 ? "0 0 6px #39FF1466" : "none" }}
              />
              {i < entries.length - 1 && <span className="w-px flex-1 bg-white/10 mt-1" />}
            </div>
            <div className="min-w-0 flex-1 pb-2.5">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-white font-semibold">
                  {e.chemistry === "li-ion" ? liIonLabel : e.chemistry === "agm" ? "Lead-acid (AGM)" : "LV battery"}
                </span>
                {i === 0 && (
                  <span className="font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: "rgba(57,255,20,0.10)", color: "#39FF14" }}>
                    current
                  </span>
                )}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">
                {new Date(e.replaced_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                {" · "}{ago(e.replaced_at)}
              </div>
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => remove(e.replaced_at)}
                disabled={deleting === e.replaced_at}
                className="shrink-0 p-1 text-zinc-600 hover:text-red-400 transition-colors disabled:opacity-40"
                aria-label="Remove entry"
                data-testid={`lv-history-delete-${i}`}
              >
                <X className="w-3 h-3" strokeWidth={2.4} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
